/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import './Login.scss';
import { Button, Form, Modal, Row, Col, Alert } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import {
  accountDetailsRequestAction,
  loginActionRequest,
  verify2faRequestAction,
  clearLoginStateAction,
} from '../../store/account/userActions';
import { useDispatch } from 'react-redux';
import { InputGroup } from 'react-bootstrap';
import MercurXIcons from '../../components/MercurXIcons/MercurXIcons';

function Login({ ...props }) {
  const { loginRequest, user, verify2faRequest, accountDetailsRequest, clearLoginState } = props;

  const [state, setState] = useState({
    data: {
      email: '',
      password: '',
    },
    errors: {},
  });

  const [modalState, setModalState] = useState({
    showModal: false,
    inputValue: '',
    isVerifying: false,
    verificationError: ''
  });

  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    if (user?.result?.two_factor_auth) {
      setModalState(prev => ({
        ...prev,
        showModal: true,
        inputValue: '',
        verificationError: ''
      }));
    }
  }, [user?.result?.two_factor_auth]);

  useEffect(() => {
    if (user?.isAuthenticated) {
      handleCloseModal();
      setIsLoggingIn(false);
    }
  }, [user?.isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoggingIn) return;

    const { data } = state;
    const errors = validate();

    if (Object.keys(errors).length > 0) {
      setState(prev => ({ ...prev, errors }));
      return;
    }

    setIsLoggingIn(true);
    
    try {
      const payload = {
        data: {
          username: data.email,
          password: data.password,
        },
        history,
      };
      
      await loginRequest(payload);
      
      if (!user?.result?.two_factor_auth) {
        setTimeout(() => {
          accountDetailsRequest();
        }, 1000);
      }
    } catch (error) {
    } finally {
      setIsLoggingIn(false);
    }
  };

  const validate = () => {
    const { data } = state;
    const errors = {};
    
    if (!data?.email || data.email.trim() === '') {
      errors.email = 'Email cannot be blank.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.email = 'Please enter a valid email address.';
      }
    }
    
    if (!data?.password || data.password.trim() === '') {
      errors.password = 'Password cannot be blank.';
    }

    return errors;
  };

  const handleChange = (e) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [e.target.id]: e.target.value,
      },
      errors: {
        ...prev.errors,
        [e.target.id]: '',
      },
    }));
  };

  const handleCloseModal = () => {
    setModalState(prev => ({
      ...prev,
      showModal: false,
      inputValue: '',
      verificationError: '',
      isVerifying: false
    }));
    
    setIsLoggingIn(false);
    
    if (clearLoginState) {
      clearLoginState();
    }
  };

  const handleVerify = async () => {
    if (modalState.isVerifying) return;

    const code = modalState.inputValue.replace(/-/g, '');
    
    if (code.length !== 6) {
      setModalState(prev => ({
        ...prev,
        verificationError: 'Please enter a complete 6-digit code.'
      }));
      return;
    }

    setModalState(prev => ({
      ...prev,
      isVerifying: true,
      verificationError: ''
    }));

    try {
      const payload = {
        userId: user?.id || user?.result?.id,
        totp_code: code,
        is_enabled: user?.two_factor_auth || user?.result?.two_factor_auth,
        history,
        loginPayload: {
          username: state.data.email,
          password: state.data.password,
        },
      };

      await verify2faRequest(payload);
      
      handleCloseModal();
      
      setTimeout(() => {
        accountDetailsRequest();
      }, 1000);

    } catch (error) {
      setModalState(prev => ({
        ...prev,
        verificationError: 'Invalid verification code. Please try again.',
        isVerifying: false
      }));
    }
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    value = value.replace(/-/g, '');
    value = value.replace(/[^0-9]/g, '').slice(0, 6);
    
    if (value.length > 3) {
      value = value.slice(0, 3) + '-' + value.slice(3);
    }
    
    setModalState(prev => ({
      ...prev,
      inputValue: value,
      verificationError: ''
    }));
  };

  const handleModalEnterKeyPress = (e) => {
    if (e.key === 'Enter' && !modalState.isVerifying) {
      handleVerify();
    }
  };

  const { data, errors } = state;

  return (
    <Row className="d-flex justify-content-center align-items-center">
      <Col className="public-layout-col d-flex justify-content-center align-items-center bg-white px-1 py-2">
        <div className="public-layout-image d-md-flex d-none flex-column justify-content-center align-items-center bg-primary px-1 py-2">
          <div className="text-fs-head-lg text-center text-light px-2">
            Welcome to the exciting world of <span>MercurX</span>
          </div>
          <div className="text-fs-body-md text-center text-light px-2">
            Step into the dynamic sphere of MercurX, where each exploration promises fascinating
            discoveries in the world of digital currency and blockchain!
          </div>
        </div>
        <div className="d-flex flex-column justify-content-center aling-items-center px-4 mx-2 public-layout-form">
          <Form id="loginForm" onSubmit={handleSubmit}>
            <Form.Group controlId="email">
              <Form.Label className="text-fs-body-lg text-t-head-color">Email address</Form.Label>
              <Form.Control
                name="email"
                type="email"
                placeholder="Enter email"
                onChange={handleChange}
                value={data?.email}
                isInvalid={!!errors?.email}
                disabled={isLoggingIn}
                autoComplete="email"
              />
              <Form.Label className="text-danger text-fs-body-sm mercurx-error-label">
                {errors?.email && '*' + errors?.email}
              </Form.Label>
            </Form.Group>

            <Form.Group controlId="password">
              <Form.Label className="text-fs-body-lg text-t-head-color">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  name="password"
                  type={passwordVisibility ? 'text' : 'password'}
                  placeholder="Password"
                  onChange={handleChange}
                  value={data?.password}
                  isInvalid={!!errors?.password}
                  disabled={isLoggingIn}
                  autoComplete="current-password"
                />
                <InputGroup.Text
                  id="basic-addon2"
                  className="d-flex justify-content-between align-items-center input-group-text-custom"
                >
                  <Col>
                    <div onClick={() => setPasswordVisibility((prevVisibility) => !prevVisibility)}>
                      <MercurXIcons
                        name={passwordVisibility ? 'BsEyeSlash' : 'BsEye'}
                        iconClassName="mercurx-password-icon"
                      />
                    </div>
                  </Col>
                </InputGroup.Text>
              </InputGroup>
              <Form.Label className="text-danger text-fs-body-sm mercurx-error-label">
                {errors?.password && '*' + errors?.password}
              </Form.Label>
            </Form.Group>

            <Button
              className="mb-4 mt-3 bg-mercurx-primary col-sm-12"
              variant="primary"
              type="submit"
              disabled={isLoggingIn}
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </Button>
          </Form>

          <NavLink
            className="forgotpassword-link d-flex justify-content-center m-2 text-primary"
            to="/forgotpassword"
          >
            Forgot Password?
          </NavLink>

          <Col className="m-1 d-flex justify-content-center">
            <NavLink className="text-fs-body-md text-t-body-color" to="/signup">
              Don't have an account yet?
            </NavLink>
            &nbsp;
            <NavLink className="signup-link text-primary" to="/signup">
              SignUp
            </NavLink>
          </Col>
        </div>
      </Col>

      <Modal 
        show={modalState.showModal} 
        onHide={handleCloseModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>Two Factor Authentication</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p className="text-muted mb-3">
              Please enter the 6-digit verification code from your authenticator app.
            </p>
            
            {modalState.verificationError && (
              <Alert variant="danger" className="py-2">
                {modalState.verificationError}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Verification Code:</Form.Label>
              <Form.Control
                type="text"
                placeholder="000-000"
                value={modalState.inputValue}
                onChange={handleInputChange}
                onKeyPress={handleModalEnterKeyPress}
                disabled={modalState.isVerifying}
                className="text-center"
                style={{ fontSize: '18px', letterSpacing: '2px' }}
                maxLength={7}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="danger" 
            onClick={handleCloseModal}
            disabled={modalState.isVerifying}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleVerify}
            disabled={modalState.isVerifying || modalState.inputValue.replace(/-/g, '').length !== 6}
          >
            {modalState.isVerifying ? 'Verifying...' : 'Verify'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.userReducer.user || { two_factor_auth: false },
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loginRequest: (creds) => {
      dispatch(loginActionRequest(creds));
    },
    accountDetailsRequest: () => {
      dispatch(accountDetailsRequestAction());
    },
    verify2faRequest: (payload) => {
      dispatch(verify2faRequestAction(payload));
    },
    clearLoginState: (payload) => {
      dispatch(clearLoginStateAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);